import { Box, Button, Container, Grid, GridItem, HStack, Text, VStack, Badge, useBreakpointValue } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCheckCircle } from 'react-icons/fa';
import CTAButton from '../CTAButton';

const MotionBox = motion(Box);

const GetHiredHero = ({ data, onEnrollClick, onWebinarClick }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  const scrollToWebinar = () => {
    const element = document.getElementById('webinar-register');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box
      position="relative"
      minH={{ base: 'auto', md: '100vh' }}
      bg={`linear-gradient(to right, rgba(10, 22, 40, 0.95) 1%, rgba(10, 22, 40, 0.4) 100%), url(${data.heroImage})`}
      bgSize="cover"
      bgPosition="right center"
      bgAttachment={{ base: 'scroll', md: 'fixed' }}
      py={{ base: 8, md: 20 }}
      display="flex"
      alignItems="center"
    >
      <Container maxW="6xl" w="full">
        <Grid
          templateColumns={{ base: '1fr', md: '1fr 1fr' }}
          gap={{ base: 8, md: 12 }}
          alignItems="center"
        >
          {/* Left Column: Text & CTA */}
          <GridItem>
            <VStack align="start" spacing={6}>
              {/* Badge */}
              <Badge
                px={4}
                py={2}
                bg="accent.green"
                color="white"
                fontSize="sm"
                fontWeight="600"
                borderRadius="full"
              >
                📈 2026 Job Market
              </Badge>

              {/* Headline */}
              <Box>
                <Text
                  as="h1"
                  fontSize={{ base: '2xl', md: '4xl' }}
                  fontWeight="900"
                  color="white"
                  lineHeight="1.2"
                  mb={4}
                >
                  {data.headline}
                </Text>
              </Box>

              {/* Subheading */}
              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                color="gray.300"
                lineHeight="1.6"
                maxW="md"
              >
                {data.subheading}
              </Text>

              {/* CTAs */}
              <HStack
                spacing={4}
                pt={4}
                flexDirection={{ base: 'column', sm: 'row' }}
                w="full"
                alignItems="flex-start"
              >
                <Button
                  as="a"
                  href="#webinar-register"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToWebinar();
                  }}
                  bg="accent.green"
                  color="white"
                  size="lg"
                  fontSize="md"
                  fontWeight="700"
                  px={8}
                  _hover={{
                    bg: 'accent.gold',
                    transform: 'translateY(-2px)',
                  }}
                  transition="all 0.3s ease"
                >
                  See Live Demo (Free) →
                </Button>

                <CTAButton
                  to={`/enroll?courseId=1`}
                  variant="outline"
                  borderColor="accent.gold"
                  color="accent.gold"
                  size="lg"
                  fontSize="md"
                  fontWeight="700"
                  px={8}
                  _hover={{
                    bg: 'accent.gold',
                    color: 'brand.800',
                  }}
                  transition="all 0.3s ease"
                >
                  Enroll Now
                </CTAButton>
              </HStack>

              {/* Trust Strip */}
              <VStack align="start" spacing={3} pt={4} w="full">
                <HStack spacing={6} flexWrap="wrap" fontSize="sm" color="gray.400">
                  <HStack spacing={2}>
                    <FaCheckCircle color="#10B981" size={16} />
                    <Text>{data.placementRate}% Placement Rate</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <FaCheckCircle color="#10B981" size={16} />
                    <Text>{data.studentCount}+ Students</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <FaCheckCircle color="#10B981" size={16} />
                    <Text>Starts {data.startDate}</Text>
                  </HStack>
                </HStack>
                <Text fontSize="xs" color="gray.500" fontStyle="italic">
                  {data.originalPrice} → ₹{data.coursePrice}
                </Text>
              </VStack>
            </VStack>
          </GridItem>

          {/* Right Column: Image + Badge (Desktop only) */}
          {!isMobile && (
            <GridItem display="flex" justifyContent="center" alignItems="center">
              <MotionBox
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                position="relative"
              >
                <Box
                  position="absolute"
                  top={-20}
                  right={-20}
                  bg="accent.green"
                  color="white"
                  px={6}
                  py={3}
                  borderRadius="lg"
                  fontWeight="700"
                  fontSize="sm"
                  zIndex={10}
                  boxShadow="0 8px 16px rgba(16, 185, 129, 0.3)"
                >
                  85% Placement Rate
                </Box>
              </MotionBox>
            </GridItem>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default GetHiredHero;
